import type { Conversation } from '$lib/js/chatwoot/types'


/**
 * @param {Blob} inputData
 */
export async function compressImage(inputData: Blob) {
	const imageBitmap = await createImageBitmap(inputData);
	let max_width = 800;
	let max_height = 800;
	let width, height;
	let blob;

	// Draw to canvas
	const canvas = document.createElement('canvas');

	if (imageBitmap.width > max_width || imageBitmap.height > max_height) {
		if (imageBitmap.width / max_width > imageBitmap.height / max_height) {
			width = max_width
			height = max_width / imageBitmap.width * imageBitmap.height
		} else {
			width = max_height / imageBitmap.height * imageBitmap.width
			height = max_height
		}
		canvas.width = width;
		canvas.height = height;
		const ctx = canvas.getContext('2d');
		if (ctx) {
			ctx.drawImage(imageBitmap, 0, 0, width, height);
		}
		// Turn into Blob
		blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 1));
	} else {
		blob = inputData;
	}

	return blob;
}

/**
 * Merge Chatwoot messages for the same order in the same ticket
 */
export function mergeSameOrderMsgs(conversations: Conversation[], order_num: number) {
	let conversation_id: number | null = null

	if (conversations) {
		conversations?.map(conversation => {
			if (parseInt(conversation.custom_attributes.numero_pedido) === order_num) {
				conversation_id = conversation.id
			}
		})
	}

	return conversation_id;
}

/**
 * Add chatwoot live chat
 */
export function loadChatwoot(): Promise<boolean> {
	return new Promise((resolve, reject) => {
		const BASE_URL = "https://chatwoot.bxapp.eu";
		const script = document.createElement("script");

		script.src = `${BASE_URL}/packs/js/sdk.js`;
		script.defer = true;
		script.async = true;

		script.onload = () => {
			if (window.chatwootSDK) {
				window.chatwootSDK.run({
					websiteToken: "YaGvR5fYdrMiwzKdodwdy4wV",
					baseUrl: BASE_URL,
					darkMode: false,
					position: 'right',
					type: 'standard',
					showPopup: false
				});

				// Initial hide attempt
				const initialBubbles = document.querySelectorAll('.woot-widget-launcher, .woot--bubble-holder');
				initialBubbles.forEach(bubble => {
					bubble.style.display = 'none';
				});

				const style = document.createElement('style')
				style.textContent = `
					.woot--bubble-holder {
						display: none;
					}
					.woot-widget-bubble:not(.woot--close) {
						display: none;
					}
				`
				document.head.appendChild(style)

				resolve(true);
			} else {
				console.error("Chatwoot SDK failed to load.");
				reject(new Error("Chatwoot SDK not found."));
			}
		};

		script.onerror = () => {
			console.error("Failed to load Chatwoot script.");
			reject(new Error("Chatwoot script failed to load."));
		};

		document.head.appendChild(script);
	});
}

export function hideChatwootLauncher() {
	const bubbles = document.querySelectorAll('.woot--bubble-holder');
	bubbles.forEach(bubble => {
		bubble.style.display = 'none';
	});
}

export function showChatwootLauncher() {
	const bubbles = document.querySelectorAll('.woot-widget-launcher, .woot--bubble-holder');
	bubbles.forEach(bubble => {
		bubble.style.display = 'block';
	});
}

export function openChatwootChat() {
	if (window.$chatwoot) {
		window.$chatwoot.toggle('open');
		showChatwootLauncher(); // Ensure launcher is visible when opening

		const closeButton = document.querySelector('.woot-widget-bubble.woot--close')

		if (closeButton) {
			closeButton.addEventListener('click', () => {
				const bubble = document.querySelector('.woot--bubble-holder')
				if (bubble) {
					bubble.style.display = 'none'
				}
			})
		}
	}
}

export async function compressMediaChatwoot(file: File): Promise<Blob> {
	if (!file || !file.type) {
		throw new Error('Invalid file: missing type information');
	}

	if (file.type.startsWith('image/')) {
		return compressImageChatwoot(file);
	} else {
		throw new Error('Unsupported file type. Please select a valid video or image file.');
	}
}

async function compressImageChatwoot(file: File): Promise<Blob> {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.src = URL.createObjectURL(file);

		img.onload = () => {
			const canvas = document.createElement('canvas');
			const ctx = canvas.getContext('2d');

			let width = img.width;
			let height = img.height;
			const maxDimension = 800;

			if (width > height && width > maxDimension) {
				height = (height * maxDimension) / width;
				width = maxDimension;
			} else if (height > maxDimension) {
				width = (width * maxDimension) / height;
				height = maxDimension;
			}

			canvas.width = width;
			canvas.height = height;
			ctx?.drawImage(img, 0, 0, width, height);

			canvas.toBlob(
				(blob) => {
					if (blob) {
						resolve(blob);
					} else {
						reject(new Error('Canvas to Blob conversion failed'));
					}
				},
				'image/jpeg',
				0.8
			);

			URL.revokeObjectURL(img.src);
		};

		img.onerror = () => {
			reject(new Error('Image loading failed'));
		};
	});
}
